import { useEffect, useRef, useState } from "react";
import "./BootSplash.css";

export interface BootSplashProps {
  onDone: () => void;
}

const FILL_MS = 2400; // matches app.normax.rs `login-nx-fill`
const FADE_MS = 400;
const REDUCED_HOLD_MS = 300;
const HARD_CAP_MS = FILL_MS + 1500;

// NX monogram clip path — lifted verbatim from app.normax.rs, viewBox "70 70 132 90".
const NX_PATH =
  "M176 75H197L169.5 111L158.5 97.5Z M124 90H144L195 155H175L155 131L136 155H116L145 117.5Z M81 75H94L134 123L123.5 136L93 99.5V155H79L75 151V81Z";

const isDisabled = () =>
  import.meta.env.MODE === "test" ||
  Boolean(import.meta.env.VITEST) ||
  import.meta.env.VITE_DISABLE_BOOT_SPLASH === "true";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false);

export function BootSplash({ onDone }: BootSplashProps) {
  const [phase, setPhase] = useState<"fill" | "out">("fill");
  const doneRef = useRef(false);
  const fadeTimeoutRef = useRef<number | undefined>(undefined);
  const reduced = prefersReducedMotion();
  const disabled = isDisabled();

  useEffect(() => {
    if (disabled && !doneRef.current) {
      doneRef.current = true;
      onDone();
    }
  }, [disabled, onDone]);

  useEffect(() => {
    if (disabled) return;
    const finish = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      setPhase("out");
      fadeTimeoutRef.current = window.setTimeout(onDone, reduced ? REDUCED_HOLD_MS : FADE_MS);
    };
    const fillT = window.setTimeout(finish, reduced ? REDUCED_HOLD_MS : FILL_MS);
    const capT = window.setTimeout(finish, HARD_CAP_MS);
    return () => {
      window.clearTimeout(fillT);
      window.clearTimeout(capT);
      window.clearTimeout(fadeTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (disabled) return null;

  return (
    <div
      data-testid="nx-boot-splash"
      className={["nx-boot-splash", `nx-boot-splash--${phase}`, reduced ? "nx-boot-splash--reduced" : ""]
        .filter(Boolean)
        .join(" ")}
      role="presentation"
    >
      <svg className="nx-boot-splash__mark" viewBox="70 70 132 90" aria-hidden="true">
        <defs>
          <clipPath id="nx-boot-shape">
            <path d={NX_PATH} />
          </clipPath>
        </defs>
        <g clipPath="url(#nx-boot-shape)">
          <rect x="70" y="70" width="132" height="90" className="nx-boot-splash__track" />
          <rect x="70" y="70" width="132" height="90" className="nx-boot-splash__fill" />
        </g>
      </svg>
    </div>
  );
}
